import { useLang } from "@/contexts/LangContext";
import SectionReveal from "@/components/SectionReveal";
import { Briefcase, Crown, Landmark, Users, ArrowRight } from "lucide-react";

const WHATSAPP = "971589656886";

const EXPERIENCES = [
  {
    id: "business",
    icon: Briefcase,
    name: "Business China Explorer",
    audience: "Entrepreneurs · Investors · Sourcing · Executives",
    image: "/img/services/calligraphy.png", // will use a more business one if available
    color: "bg-[#0E2A5C]",
    accent: "text-[#D4AF37]",
    inclusions: [
      "Factory visits & supplier matching",
      "Trade fair assistance (Canton Fair, CIIE)",
      "Executive travel planning",
      "B2B networking events",
      "Bilingual interpreter & contract liaison",
    ],
    cta: "Request Business Travel Consultation",
  },
  {
    id: "luxury",
    icon: Crown,
    name: "Luxury China Discovery",
    audience: "Discerning travelers · VIPs",
    image: "/img/destinations/shanghai.png",
    color: "bg-gradient-to-br from-[#2E5D4B] to-[#1F3D2E]",
    accent: "text-[#D4AF37]",
    inclusions: [
      "5-star and boutique heritage hotels",
      "Private bilingual guides",
      "Michelin-grade fine dining",
      "VIP cultural access (after-hours Forbidden City etc.)",
      "Helicopter & private jet options",
    ],
    cta: "Plan My Luxury Journey",
  },
  {
    id: "heritage",
    icon: Landmark,
    name: "Heritage & Culture Journey",
    audience: "Beijing · Xi'an · Chengdu",
    image: "/img/destinations/xian.png",
    color: "bg-[#0A0A0A]",
    accent: "text-[#D4AF37]",
    inclusions: [
      "Forbidden City, Great Wall, Summer Palace",
      "Terracotta Army & Tang dynasty Xi'an",
      "Giant pandas & Sichuan opera in Chengdu",
      "Calligraphy & tea ceremony workshops",
      "Traditional courtyard hotel stays",
    ],
    cta: "Discover Heritage China",
  },
  {
    id: "family",
    icon: Users,
    name: "Family China Adventure",
    audience: "Families · Kids 6-16",
    image: "/img/destinations/zhangjiajie.png",
    color: "bg-[#C62828]",
    accent: "text-[#D4AF37]",
    inclusions: [
      "Avatar Mountains in Zhangjiajie",
      "Shanghai Disney & ocean parks",
      "Kid-friendly hands-on culture workshops",
      "Halal & vegetarian dining curated",
      "Educational add-ons: zoos, science museums",
    ],
    cta: "Plan My Family Trip",
  },
];

const FeaturedExperiences = () => {
  const { t } = useLang();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const whatsappLink = (exp) =>
    `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
      `Hi The China Explorer! I'm interested in the "${exp.name}" experience. Please share details and a proposal.`
    )}`;

  return (
    <section
      id="experiences"
      data-testid="experiences-section"
      className="bg-[#EAE6DF] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionReveal className="max-w-3xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#2E5D4B] font-semibold mb-4">
            ✦ Featured Experiences
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#121212] leading-[1.05]">
            Four ways to fall in love with China.
          </h2>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">
            Choose the journey that matches your purpose — business, luxury, heritage or
            family — and we'll build everything around it.
          </p>
        </SectionReveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-6 md:gap-8">
          {EXPERIENCES.map((exp, i) => {
            const Icon = exp.icon;
            return (
              <SectionReveal
                key={exp.id}
                delay={i * 0.1}
                data-testid={`experience-card-${exp.id}`}
                className={`relative overflow-hidden ${exp.color} text-white p-8 md:p-10 group`}
              >
                <div
                  className="absolute inset-0 opacity-15 bg-cover bg-center group-hover:opacity-25 transition-opacity duration-700"
                  style={{ backgroundImage: `url(${exp.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-black/30" />

                <div className="relative">
                  <div className="flex items-center gap-3">
                    <span className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center">
                      <Icon size={20} className={exp.accent} />
                    </span>
                    <p className={`text-xs uppercase tracking-[0.25em] font-semibold ${exp.accent}`}>
                      {exp.audience}
                    </p>
                  </div>
                  <h3 className="mt-6 font-heading text-3xl md:text-4xl leading-tight">
                    {exp.name}
                  </h3>
                  <ul className="mt-6 space-y-2">
                    {exp.inclusions.map((inc, j) => (
                      <li key={j} className="text-sm text-white/85 flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0" />
                        {inc}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <a
                      data-testid={`exp-${exp.id}-whatsapp`}
                      href={whatsappLink(exp)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-white text-[#0E2A5C] hover:bg-[#F7F5F0] px-5 py-3 uppercase tracking-wider text-xs font-bold transition-colors"
                    >
                      {exp.cta} <ArrowRight size={14} />
                    </a>
                    <button
                      data-testid={`exp-${exp.id}-contact`}
                      onClick={() => scrollTo("contact")}
                      className="inline-flex items-center gap-2 border border-white/30 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white px-5 py-3 uppercase tracking-wider text-xs font-semibold transition-colors"
                    >
                      Get Free Consultation
                    </button>
                  </div>
                </div>
              </SectionReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedExperiences;
